import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {updateProfile} from '../../../../store/actions/index';
import moment from 'moment';
import classNames from 'class-names';
import {Container, Row} from 'react-bootstrap';
import Loader from 'react-loader-spinner';
import {useDispatch, useSelector } from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';

import Col from '../../../../components/Col/Col';
import TextInput from '../../../../components/ui/TextInput/TextInput';
import ButtonInput from '../../../../components/ui/ButtonInput/ButtonInput';
import PhoneNumberInput from '../../../../components/ui/PhoneNumberInput/PhoneNumberInput';
import SubmitButton from '../../../../components/ui/SubmitButton/SubmitButton';
import ScreenFooter from '../../../../components/ScreenFooter/ScreenFooter';

import Styles from './EditAccount.module.scss';

import { ROUTER_URL } from '../../../../const/ROUTER_URL';
import { formatPhoneNumber } from '../../../../helpers/function/format';

const MAX_LENGTH_FIRST_NAME = 24;
const MAX_LENGTH_LAST_NAME = 24;

const EditAccount = () => {
    const {t} = useTranslation('common');

    const history = useHistory();

    const dispatch = useDispatch();

    const style = useSelector(state => state.widget.style);
    const profile = useSelector(state => state.profile.profile);
    const editAccountValidationErrors = useSelector(state => state.profile.editAccountValidationErrors);


    const [disabled, setDisabled] = useState(true)
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoading, setLoading] = useState(false);

    const initialValues = useMemo(() => ({
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        contactNumber: profile?.contactNumber || '',
        companyName: profile?.companyName || '',
        address: profile?.address || '',
        zipCode: profile?.zipCode || '',
        city: profile?.city || '',
        dateOfBirth: profile?.dateOfBirth || '',
        extraFieldValue: profile?.extraFieldValue || '',
    }), [profile])

    const validationSchema = useMemo(() => yup.object({
        firstName: yup
            .string()
            .required(t('errors.cannotBeEmptyWithName', { name: t('myProfile.editAccount.firstName') }))
            .max(
                MAX_LENGTH_FIRST_NAME,
                t(
                    'errors.maxLengthWithName',
                    {
                        name: t('myProfile.editAccount.firstName'),
                        length: MAX_LENGTH_FIRST_NAME,
                    }
                ),
            ),
        lastName: yup
            .string()
            .required(t('errors.cannotBeEmptyWithName', { name: t('myProfile.editAccount.lastName') }))
            .max(
                MAX_LENGTH_LAST_NAME,
                t(
                    'errors.maxLengthWithName',
                    {
                        name: t('myProfile.editAccount.lastName'),
                        length: MAX_LENGTH_LAST_NAME,
                    }
                ),
            ),
        contactNumber: yup
            .string(),
        companyName: yup
            .string()
            .nullable(),
        address: yup
            .string(),
        dateOfBirth: yup
            .string(),
        zipCode: yup
            .string(),
        city: yup
            .string(),
        extraFieldValue: yup
            .string()
            .nullable(),
    }), [t])

    const {
        values,
        errors,
        isValid,
        setFieldValue,
        handleSubmit,
    } = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: useCallback((values) => {
            setLoading(true);
            dispatch(updateProfile({
                firstName: values.firstName,
                lastName: values.lastName,
                birthDate: values.dateOfBirth,
                phoneNumber: formatPhoneNumber(values.contactNumber),
                address: values.address,
                zipCode: values.zipCode,
                city: values.city,
                companyName: values.companyName,
                extraFieldValue: values.extraFieldValue,
            }))
        }, [dispatch])
    })

    const onSubmit = useCallback(() => handleSubmit(), [handleSubmit])

    const handeFirstNameInput = useCallback((value) => {
        setFieldValue('firstName', value);
        setValidationErrors((prev) => ({...prev, name: null}));
        setDisabled(false)
    }, [setFieldValue]);

    const handeLastNameInput = useCallback((value) => {
        setFieldValue('lastName', value);
        setValidationErrors((prev) => ({...prev, lastName: null}));
        setDisabled(false)
    }, [setFieldValue]);

    const handeContactNumberInput = useCallback((value) => {
        setFieldValue('contactNumber', value);
        setValidationErrors((prev) => ({...prev, contactNumber: null}));
        setDisabled(false)
    }, [setFieldValue]);

    const handleProfileAccountPasswordRedirect = useCallback(() => {
        history.push(ROUTER_URL.PROFILE_ACCOUNT_PASSWORD);
    }, [history]);

    const handeCompanyNameInput = useCallback((value) => {
        setFieldValue('companyName', value);
        setValidationErrors((prev) => ({...prev, companyName: null}));
        setDisabled(false);
    }, [setFieldValue]);

    const handeAddressInput = useCallback((value) => {
        setFieldValue('address', value);
        setValidationErrors((prev) => ({...prev, address: null}));
        setDisabled(false);
    }, [setFieldValue]);

    const handeZipCodeInput = useCallback((value) => {
        setFieldValue('zipCode', value);
        setValidationErrors((prev) => ({...prev, zipCode: null}));
        setDisabled(false);
    }, [setFieldValue]);

    const handeCityInput = useCallback((value) => {
        setFieldValue('city', value);
        setValidationErrors((prev) => ({...prev, city: null}));
        setDisabled(false);
    }, [setFieldValue]);

    const handeExtraFieldValueInput = useCallback((value) => {
        setFieldValue('extraFieldValue', value);
        setValidationErrors((prev) => ({...prev, extraField: null}));
        setDisabled(false);
    }, [setFieldValue]);

    useEffect(() => {
        setLoading(false);
        setDisabled(true)
    }, [profile]);

    useEffect(() => {
        setValidationErrors(editAccountValidationErrors);
        setLoading(false);
    }, [editAccountValidationErrors]);

    if(!profile) {
        return (
            <div className={Styles.container}>
                <Loader type="TailSpin" color="#000" height={40} width={40}/>
            </div>
        )
    }

    return (
        <>
            <div
                className={Styles.container}
                style={{paddingBottom:  (style === 'dev' || style === 'website')  ? '112px' : ''}}
            >
                <Container className={'px-0'}>
                    <Row className={Styles.columnContainer}>
                        <Col className={Styles.column} xs={12} md={6}>
                            <TextInput
                                value={values.firstName}
                                data-testid='nameInput'
                                setInputValue={handeFirstNameInput}
                                name={t('myProfile.editAccount.firstName')}
                                validationError={validationErrors.name || errors.firstName}
                            />
                            <TextInput
                                value={values.lastName}
                                data-testid='lastNameInput'
                                setInputValue={handeLastNameInput}
                                name={t('myProfile.editAccount.lastName')}
                                validationError={validationErrors.lastName || errors.lastName}
                            />
                            <TextInput
                                value={moment.unix(values.dateOfBirth).format('D/M/Y')}
                                data-testid='birthInput'
                                disabled
                                name={t('myProfile.editAccount.birthDate')}
                                validationError={validationErrors.birthDate}
                            />
                            <PhoneNumberInput
                                value={values.contactNumber}
                                data-testid='phoneInput'
                                setInputValue={handeContactNumberInput}
                                name={t('myProfile.editAccount.phoneNumber')}
                                validationError={validationErrors.contactNumber}
                            />
                            <TextInput
                                value={profile.email}
                                disabled
                                data-testid='emailInput'
                                name={t('myProfile.editAccount.emailAddress')}
                            />
                            <ButtonInput
                                data-testid='passwordInput'
                                onClick={handleProfileAccountPasswordRedirect}
                                name={t('myProfile.editAccount.password')}
                            />
                        </Col>

                        <Col className={classNames(Styles.column, Styles.alignTop)} xs={12} md={6}>
                            <TextInput
                                value={values.companyName}
                                data-testid="companyNameInput"
                                setInputValue={handeCompanyNameInput}
                                name={t('myProfile.editAccount.companyName')}
                                validationError={validationErrors.companyName}
                            />
                            <TextInput
                                value={values.address}
                                data-testid='addressInput'
                                setInputValue={handeAddressInput}
                                name={t('myProfile.editAccount.address')}
                                validationError={validationErrors.address || errors.address}
                            />
                            <TextInput
                                value={values.zipCode}
                                data-testid='zipInput'
                                setInputValue={handeZipCodeInput}
                                name={t('myProfile.editAccount.zipCode')}
                                validationError={validationErrors.zipCode || errors.zipCode}
                            />
                            <TextInput
                                value={values.city}
                                data-testid='cityInput'
                                setInputValue={handeCityInput}
                                name={t('myProfile.editAccount.city')}
                                validationError={validationErrors.city || errors.city}
                            />
                            <TextInput
                                value={values.extraFieldValue}
                                data-testid="extraFieldInput"
                                setInputValue={handeExtraFieldValueInput}
                                name={profile.extraFieldLabel || 'Note'}
                                validationError={validationErrors.extraField}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>

            <ScreenFooter visible={true}>
                <SubmitButton
                    data-testid='button1'
                    submitHandler={onSubmit}
                    isLoading={isLoading}
                    disabled={disabled || !isValid}
                >
                    {t('myProfile.editAccount.save').toUpperCase()}
                </SubmitButton>
            </ScreenFooter>
        </>
    )
};

export default EditAccount;
